import {ActionIcon, Button, Flex, Paper, Space} from "@mantine/core";
import {DataTable} from "mantine-datatable";
import { ArrowClockwiseIcon, TrashIcon } from "@phosphor-icons/react";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary";
import LoaderIndicator from "../../../components/Loader/LoaderIndicator.tsx";
import Page from "../../../components/Page/Page.tsx";
import MessageContext from "../../../context/MessageContext.tsx";
import CGT from "../../../utils/CGT.ts";
import api from '../../../utils/api.ts';
import {Col, Row} from '../../../components/Flex/Flex';
import {bodyReward, bodyType} from "./Formatters";
import SwitchTab from "./SwitchTab";

const bodyClosed = (row) => {
    let date = CGT.fromSeconds(row.dateClosed);
    return <div>
        <span>{date.toCGT(true)}</span><br/>
        <small>{date.toRelative()}</small>
    </div>;
}

const bodyStatus = row => {
    let color = '';
    if(row.status === 'complete' || row.status === 'rewarded') {
        color = 'text-green';
    } else if(row.status === 'failed') {
        color = 'text-red';
    }
    return <div className={`${color} contract-status`}>{row.status}</div>
}


function ArchiveList() {
    const [contracts, setContracts] = useState(null);
    const {success, error} = useContext(MessageContext);
    const [sortStatus, setSortStatus] = useState({
        columnAccessor: 'dateClosed',
        direction: 'asc',
    });


    useEffect(() => {
        api.get('/bh/contracts/closed').then(res => {
            setContracts(res.data);
        })
    }, []);

    const sortedRecords = useMemo(() => {
        if (!contracts) return [];
        const sorted = [...contracts].sort((a, b) => {
            const accessor = sortStatus.columnAccessor;
            const aVal = a[accessor] ?? '';
            const bVal = b[accessor] ?? '';
            const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : (aVal - bVal);
            return sortStatus.direction === 'desc' ? -cmp : cmp;
        });
        return sorted;
    }, [contracts, sortStatus]);


    const bodyActions = useCallback(row => {
        return (
            <Row mb={0}>
                <ActionIcon variant="subtle" onClick={() => {
                    api.post('/bh/contract/' + row.id+'/active').then(() => {
                        success('Contract reset', 'Contract is now active again');
                        setContracts(prev => {
                            let next = [...prev];
                            return next.filter(c => c.id !== row.id);
                        })
                    }).catch(err => {
                        if(err.response.data) {
                            error('Reset Failed', err.response.data.error);
                        }
                    })
                }} title="Reset to active">
                    <ArrowClockwiseIcon size={16}/>
                </ActionIcon>
                <ActionIcon variant="subtle" onClick={() => {
                    if(confirm('Are you sure you want to delete this contract? You will lose all scan data')) {
                        api.delete('/bh/contract/'+row.id).then(() => {
                            setContracts(prev => {
                                let next = [...prev];
                                return next.filter(c => c.id !== row.id);
                            })
                        })
                    }}} title="Delete Contract">
                    <TrashIcon size={16}/>
                </ActionIcon>
            </Row>
        )
    }, []);
    const stats = useMemo(() => {
        if(contracts) {
            return contracts.reduce((stat, el) => {
                stat[el.status] += 1;
                return stat;
            }, {failed: 0, complete: 0, rewarded: 0});
        } else {
            return {failed: 0, complete: 0, rewarded: 0};
        }
    }, [contracts]);


    if(contracts === null) {
        return (<Page>
            <LoaderIndicator id="page-load" message="Loading contracts" />
        </Page>)
    }

    return (
        <Page>
            <title>Archived Contracts</title>
            <SwitchTab left={<h2 className="m-0">Archived Contracts</h2>} />
            <Paper className="mt-2">
                <Flex justify="space-around">
                    <Col>Total contracts closed: {contracts.length}</Col>
                    <Col>Contracts completed: <span className="text-green">{stats.complete}</span></Col>
                    <Col>Contracts rewarded: <span className="text-green">{stats.rewarded}</span></Col>
                    <Col>Contracts failed: <span className="text-red">{stats.failed}</span></Col>
                </Flex>
                <Space h="md" />
                <DataTable
                    records={sortedRecords}
                    idAccessor="id"
                    sortStatus={sortStatus}
                    onSortStatusChange={setSortStatus}
                    columns={[
                        {accessor: 'name', title: 'Target Name', sortable: true},
                        {accessor: 'status', title: 'Status', render: row => bodyStatus(row)},
                        {accessor: 'dateClosed', title: 'Completed', sortable: true, render: row => bodyClosed(row)},
                        {accessor: 'difficulty', title: 'Difficulty'},
                        {accessor: 'type', title: 'Type', render: row => bodyType(row)},
                        {accessor: 'reward', title: 'Reward', render: row => bodyReward(row)},
                        {accessor: 'notes', title: 'Notes'},
                        {accessor: 'actions', title: 'Actions', render: row => bodyActions(row)},
                    ]}
                />
            </Paper>
        </Page>
    );


}


export default withRouteErrorBoundary(ArchiveList);
